'use client';

import React, { type ReactNode } from 'react';
import Button, { ButtonVariants } from '@pixum/button';
import { Dialog as WebDialog, DialogContent, DialogFooter, DialogHeader, type DIALOG_ROLES } from '@pixum/web-dialog';
import clsx from 'clsx';
import { DIALOG_TYPE } from '../../constants';
import type { DialogType } from '../../types';
import './styles.css';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  headline?: string;
  children: ReactNode;
  className?: string;
  fullScreen?: boolean;
  dialogType?: DialogType;
  /** ARIA role; `DIALOG_ROLES.ALERT` gives the alert styling. Defaults to a regular dialog. */
  role?: DIALOG_ROLES;
  /** Adds a footer text button with this label that closes the dialog, and hides the corner close button. */
  closeLabel?: string;
}

export function Dialog({
  isOpen,
  onClose,
  headline,
  children,
  className,
  fullScreen = false,
  dialogType = DIALOG_TYPE.DEFAULT,
  role,
  closeLabel
}: DialogProps) {
  const header = headline ? <DialogHeader center={<h4>{headline}</h4>} right={null} /> : null;

  const footer = closeLabel ? (
    <DialogFooter
      right={
        <Button label={closeLabel} onClick={onClose} variant={ButtonVariants.TERTIARY}>
          {closeLabel}
        </Button>
      }
      role={role}
    />
  ) : undefined;

  const dialogClassName = clsx(
    className,
    dialogType === DIALOG_TYPE.ORDER_WAYS && 'dialog__order-ways',
    dialogType === DIALOG_TYPE.SOFTWARE_DOWNLOAD && 'dialog__software-download'
  );

  return (
    <WebDialog
      className={dialogClassName}
      content={
        <DialogContent>
          <>{children}</>
        </DialogContent>
      }
      footer={footer}
      fullScreen={fullScreen}
      header={header}
      onClose={onClose}
      open={isOpen}
      role={role}
      showCloseButton={!closeLabel}
    />
  );
}
